// src/components/PriceHistogram.js
import React, { useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '@mui/material/styles';
import { animated, useSpring } from '@react-spring/web';
import Rectangle from './Rectangle';

const MARGIN = { top: 20, right: 20, bottom: 50, left: 50 };
const BUCKET_NUMBER = 10;
const BUCKET_PADDING = 2;
const SIDE_PADDING = 20; // extra space on left/right

// Gaussian kernel for density estimation
const kernelGaussian = (bandwidth) => (u) =>
    (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * (u / bandwidth) ** 2);

// Kernel density estimator generator
const kernelDensityEstimator = (kernel, X) => (V) =>
    X.map(x => [x, d3.mean(V, v => kernel(x - v))]);

const PriceHistogram = ({ width, height, data, fixedDomain }) => {
    const theme = useTheme();
    const axesRef = useRef(null);
    const boundsWidth = width - MARGIN.left - MARGIN.right;
    const boundsHeight = height - MARGIN.top - MARGIN.bottom;

    // Use the fixed domain (computed from the original search results)
    const domain = useMemo(() => fixedDomain, [fixedDomain]);

    const xScale = useMemo(() => {
        return d3
            .scaleLinear()
            .domain(domain)
            .range([SIDE_PADDING, boundsWidth - SIDE_PADDING])
            .nice();
    }, [domain, boundsWidth]);

    // Bin the (filtered) data using the fixed domain
    const buckets = useMemo(() => {
        const binGenerator = d3
            .bin()
            .domain(domain)
            .thresholds(xScale.ticks(BUCKET_NUMBER));
        return binGenerator(data);
    }, [data, domain, xScale]);

    const maxCount = useMemo(() => d3.max(buckets, d => d.length) || 0, [buckets]);

    // Although we no longer render a y–axis, we still need a yScale for the bars.
    const yScale = useMemo(() => {
        return d3.scaleLinear().domain([0, maxCount]).range([boundsHeight, 0]).nice();
    }, [maxCount, boundsHeight]);

    // Compute density data from the filtered data using the fixed xScale ticks.
    const densityData = useMemo(() => {
        if (!data || data.length === 0) return [];
        const bandwidth = (domain[1] - domain[0]) / 30;
        const kde = kernelDensityEstimator(kernelGaussian(bandwidth), xScale.ticks(100));
        return kde(data);
    }, [data, domain, xScale]);

    // Scale density values so that the maximum density equals the histogram's max count.
    const densityScaleFactor = useMemo(() => {
        const maxDensity = d3.max(densityData, d => d[1]) || 1;
        return maxCount / maxDensity;
    }, [densityData, maxCount]);

    // Create a smooth density (tendency) line.
    const densityLineGenerator = useMemo(() => {
        return d3.line()
            .curve(d3.curveBasis)
            .x(d => xScale(d[0]))
            .y(d => yScale(d[1] * densityScaleFactor));
    }, [xScale, yScale, densityScaleFactor]);

    // Animate the density path string using react-spring.
    const densityPath = densityData.length > 0 ? densityLineGenerator(densityData) : '';
    const { path: animatedPath } = useSpring({
        path: densityPath,
        config: { tension: 170, friction: 26 },
    });

    // Render only the x–axis with larger regular text (no bold) and without tick lines.
    useEffect(() => {
        const svgElement = d3.select(axesRef.current);
        svgElement.selectAll('*').remove();

        const xAxis = d3
            .axisBottom(xScale)
            .ticks(BUCKET_NUMBER)
            // format as plain number, up to one decimal, trimming trailing zeros:
            .tickFormat(d3.format(".1~f"))
            .tickSize(0);
        const xAxisGroup = svgElement
            .append('g')
            .attr('transform', `translate(0, ${boundsHeight})`)
            .call(xAxis);

        xAxisGroup.selectAll('text')
            .style('fill', theme.palette.text.primary)
            .style('font-family', theme.typography.fontFamily)
            .style('font-size', '14px');

        // X–axis Label with units "Price (USD)"
        xAxisGroup.append("text")
            .attr("x", boundsWidth / 2)
            .attr("y", 40)
            .attr("fill", theme.palette.text.primary)
            .style('font-family', theme.typography.fontFamily)
            .style('font-size', '14px')
            .attr("text-anchor", "middle")
            .text("Price (USD)");
    }, [xScale, boundsHeight, theme, boundsWidth]);

    const rects = buckets.map((bucket) => {
        if (bucket.x0 == null || bucket.x1 == null) return null;
        const x = xScale(bucket.x0) + BUCKET_PADDING / 2;
        const barWidth = Math.max(0, xScale(bucket.x1) - xScale(bucket.x0) - BUCKET_PADDING);
        const y = yScale(bucket.length);
        const barHeight = boundsHeight - yScale(bucket.length);
        return (
            <Rectangle
                key={`${bucket.x0}-${bucket.x1}`}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                theme={theme}
            />
        );
    });

    return (
        <svg width={width} height={height}>
            <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
                {rects}
                {densityData.length > 0 && (
                    <animated.path
                        d={animatedPath}
                        fill="none"
                        stroke={theme.palette.primary.light}  // lighter green version
                        strokeWidth="2"
                        strokeOpacity="0.7"
                    />
                )}
            </g>
            <g ref={axesRef} transform={`translate(${MARGIN.left}, ${MARGIN.top})`} />
        </svg>
    );
};

export default PriceHistogram;
